import { useCallback, useEffect, useState } from "react";

/**
 * Gobierna cuándo está desplegado un submenú flotante.
 *
 * Con ratón se abre al pasar por encima y se cierra al salir. A dedo no hay hover: se
 * abre y se cierra por toque, y el toque fuera del elemento lo cierra, que es lo que
 * además hace que al tocar otra sección se recoja la que estaba abierta.
 *
 * @param {object} anchorRef ref del elemento que contiene al disparador y a su panel
 * @param {boolean} isTouchInput si se está navegando a dedo
 * @returns {object} { isOpen, openPanel, closePanel, togglePanel, hoverProps }
 */
const useFlyoutTrigger = (anchorRef, isTouchInput) => {
  const [isOpen, setIsOpen] = useState(false);

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => setIsOpen((previous) => !previous), []);

  useEffect(() => {
    if (!isTouchInput || !isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!anchorRef.current?.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [anchorRef, isTouchInput, isOpen]);

  // Al cambiar de forma de interactuar (se enchufa un ratón, se toca la pantalla) no debe
  // quedarse un panel abierto que ya nadie va a cerrar con el gesto contrario
  useEffect(() => {
    setIsOpen(false);
  }, [isTouchInput]);

  const hoverProps = isTouchInput
    ? {}
    : { onMouseEnter: openPanel, onMouseLeave: closePanel };

  return { isOpen, openPanel, closePanel, togglePanel, hoverProps };
};

export default useFlyoutTrigger;
